import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { NumberPattern } from '../../types/admin';
import { Save } from 'lucide-react';

interface NumberPatternEditorProps {
  patterns: NumberPattern[];
  onUpdate: (id: string, data: Partial<NumberPattern>) => Promise<unknown>;
}

export const NumberPatternEditor: React.FC<NumberPatternEditorProps> = ({ patterns, onUpdate }) => {
  const [editing, setEditing] = useState<Record<string, Partial<NumberPattern>>>({});

  const handleChange = (id: string, field: string, value: string | number) => {
    setEditing(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (id: string) => {
    if (editing[id]) {
      await onUpdate(id, editing[id]);
      setEditing(prev => { const next = { ...prev }; delete next[id]; return next; });
    }
  };

  const generateExample = (p: NumberPattern) => {
    const year = p.year_format === 'YYYY' ? '2026' : p.year_format === 'YY' ? '26' : '';
    const counter = String(p.next_counter).padStart(p.counter_length, '0');
    return `${p.prefix}${year ? p.separator + year : ''}${p.separator}${counter}`;
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Entity</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Prefix</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Year</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Separator</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Counter Len</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Next</th>
              <th className="text-left py-3 px-3 font-semibold text-slate-400">Example</th>
              <th className="text-right py-3 px-3 font-semibold text-slate-400 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map(pat => {
              const edits = editing[pat.pattern_id];
              return (
                <tr key={pat.pattern_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-3 px-3 font-semibold text-slate-200 capitalize">{pat.entity_type.replace('_', ' ')}</td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input value={edits.prefix || pat.prefix} onChange={e => handleChange(pat.pattern_id, 'prefix', e.target.value)}
                        className="w-16 bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100" />
                    ) : (
                      <span className="font-mono text-sky-400">{pat.prefix}</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <select value={edits.year_format || pat.year_format} onChange={e => handleChange(pat.pattern_id, 'year_format', e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100">
                        <option value="">None</option>
                        <option value="YYYY">YYYY</option>
                        <option value="YY">YY</option>
                      </select>
                    ) : (
                      <span className="text-slate-300">{pat.year_format || '—'}</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input value={edits.separator || pat.separator} onChange={e => handleChange(pat.pattern_id, 'separator', e.target.value)}
                        className="w-12 bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100" />
                    ) : (
                      <span className="text-slate-300">{pat.separator}</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    {edits ? (
                      <input type="number" value={edits.counter_length || pat.counter_length} onChange={e => handleChange(pat.pattern_id, 'counter_length', Number(e.target.value))}
                        className="w-16 bg-slate-800 border border-slate-700 rounded p-1.5 text-xs text-slate-100" />
                    ) : (
                      <span className="text-slate-300">{pat.counter_length}</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-slate-300">{pat.next_counter}</td>
                  <td className="py-3 px-3 font-mono text-emerald-400 text-[11px]">
                    {generateExample(pat)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {edits ? (
                      <Button variant="primary" size="sm" onClick={() => handleSave(pat.pattern_id)} icon={<Save className="w-3 h-3" />}>Save</Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(prev => ({ ...prev, [pat.pattern_id]: {} }))}>Edit</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
